import { CareerId } from '@common/const/catalog/career';
import { StepType, TrainingAnswers, TrainingAnswersBlock, TrainingBlockId, TrainingStep } from '@common/model/client/training/types';
import { nannyFlowFactory } from './factories/nanny';
import { TrainingBlockModel } from './block';

export type FlowFactory = () => TrainingBlockModel[];

export type BlockMeta = {
  title: string;
  id: TrainingBlockId;
  stepNumber: number;
  totalSteps: number;
};

export enum FlowStepType {
  Welcome,
  Finish,
  BlockResult,
  BlockStep,
}

export type FlowStepBlockResult = {
  type: FlowStepType.BlockResult;
  errorsCount: number;
};

export interface FlowStep {
  type: FlowStepType;
}

export interface FlowStepBlockStep extends FlowStep {
  type: FlowStepType.BlockStep;
  meta: BlockMeta;
  step: TrainingStep;
  validate: (answer: TrainingAnswersBlock) => boolean;
}
type AnyFlowStep = FlowStep | FlowStepBlockStep | FlowStepBlockResult;
export type Flow = Generator<AnyFlowStep, AnyFlowStep, TrainingAnswersBlock | undefined>;

export type GeneratorStep = {
  done?: boolean;
  value: AnyFlowStep;
};

export const getFlowFactory = (careerId: CareerId): FlowFactory => {
  switch (careerId) {
    case CareerId.Nanny:
      return nannyFlowFactory;
    default:
      throw new Error(`No training flow for career: ${careerId}`);
  }
};

// eslint-disable-next-line func-style
export function* generateFlow(careerId: CareerId, initialAnswers: TrainingAnswers = {}): Flow {
  const flow = getFlowFactory(careerId)();
  if (!Object.keys(initialAnswers).length) {
    yield { type: FlowStepType.Welcome };
  }
  let blockIndex = 0;
  let block = flow[blockIndex];
  let blockAnswers: TrainingAnswersBlock | undefined;
  let blockErrorsCount: number;

  while (block) {
    if (initialAnswers[block.id]) {
      blockErrorsCount = block.complete(initialAnswers[block.id]);
      if (!blockErrorsCount) {
        block = flow[++blockIndex];
        continue;
      }
    }

    for (const step of block) {
      blockAnswers = yield {
        type: FlowStepType.BlockStep,
        step,
        validate: (answers: TrainingAnswersBlock) => (step.stepType === StepType.Test ? block.validateStep(step, answers[step.id]) : true),
        meta: {
          id: block.id,
          title: block.title,
          stepNumber: block.currentStep,
          totalSteps: block.totalSteps,
        },
      };
    } // steps

    blockErrorsCount = block.complete(blockAnswers || {});
    yield { type: FlowStepType.BlockResult, errorsCount: blockErrorsCount };
    if (!blockErrorsCount) {
      block = flow[++blockIndex];
    } else {
      block.reset();
    }
  } // blocks

  return { type: FlowStepType.Finish };
}
